import { useEffect, useState } from "react";
import { X, Megaphone } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";

/**
 * Announcement strip.
 *
 * Sits under the navbar at the top of the page and scrolls away with it. It used to be
 * position:fixed, which made it a second permanent bar on a phone, let it cover the first
 * lines of every page, and needed a body padding hack that pushed the whole page down 36px
 * a couple of seconds after load (a 0.04 layout shift on every route, Lighthouse 2026-09-30).
 *
 * How the layout works: the wrapper is an ordinary block, so it takes up exactly its own
 * height in the page flow and everything below moves down by that much. Navbar is mounted
 * first on every page, so that block starts at the very top, underneath the fixed navbar.
 * The inner strip is shifted down by the navbar's height (relative top-14 / lg:top-16) so it
 * shows just below the navbar, in the gap pages already leave for it (pt-20 and friends).
 * Nothing is measured and nothing is fixed. The wrapper carries 8px of bottom margin so pages
 * with a tight top padding (pt-14) still get a gap under the strip; index.html mirrors it.
 *
 * What shows: an announcement set in site_settings (announcement_enabled + announcement_text)
 * wins. With none set, the default community line shows. Either can be dismissed, and the
 * dismissal is remembered per message.
 *
 * No layout shift: the first render already knows what to show. The default line needs no
 * request, and the last announcement fetched is kept in localStorage so a returning visitor
 * gets it on the first frame too.
 */

type Banner = { text: string; linkUrl: string; linkLabel: string; isDefault: boolean };

const DEFAULT_BANNER: Banner = {
  text: "Compare notes with other researchers. Join [r/BodyHackGuide](https://reddit.com/r/bodyhackguide)",
  linkUrl: "",
  linkLabel: "",
  isDefault: true,
};
// The default line on a phone. One line at 375px, so the strip never wraps.
const DEFAULT_BANNER_SHORT = "Compare notes on [r/BodyHackGuide](https://reddit.com/r/bodyhackguide)";

const CACHE_KEY = "bhg_announcement_v1";

function dismissKey(b: Banner): string {
  if (b.isDefault) return "banner_dismissed_default_v1";
  try {
    return `banner_dismissed_${btoa(b.text).slice(0, 16)}`;
  } catch {
    // btoa throws on characters outside Latin-1. Fall back to a length-and-prefix key.
    return `banner_dismissed_${b.text.length}_${b.text.slice(0, 8)}`;
  }
}

function isDismissed(b: Banner): boolean {
  try {
    return !!localStorage.getItem(dismissKey(b));
  } catch {
    return false;
  }
}

/** What to show before any request comes back: the cached announcement, else the default. */
function initialBanner(): Banner | null {
  if (typeof window === "undefined") return null;
  let candidate: Banner = DEFAULT_BANNER;
  try {
    const cached = JSON.parse(localStorage.getItem(CACHE_KEY) || "null");
    if (cached && typeof cached.text === "string" && cached.text.trim()) {
      candidate = { text: cached.text, linkUrl: cached.linkUrl || "", linkLabel: cached.linkLabel || "", isDefault: false };
    }
  } catch {
    /* unreadable cache: show the default */
  }
  return isDismissed(candidate) ? null : candidate;
}

function renderBannerInline(text: string, linkUrl?: string, linkLabel?: string): React.ReactNode {
  let processedText = text;
  if (linkUrl && linkLabel) {
    processedText = text.replace(linkLabel, `[${linkLabel}](${linkUrl})`);
    if (processedText === text) {
      processedText = `${text} [${linkLabel}](${linkUrl})`;
    }
  }

  const parts: React.ReactNode[] = [];
  let remaining = processedText;
  let key = 0;
  const linkCls = "text-primary underline underline-offset-2 font-semibold hover:opacity-80 transition-opacity";

  while (remaining.length > 0) {
    const boldMatch = remaining.match(/\*\*(.+?)\*\*/);
    const linkMatch = remaining.match(/\[([^\]]+)\]\(([^)]+)\)/);
    const urlMatch = remaining.match(/(https?:\/\/[^\s\]]+)/);

    const matches = [
      boldMatch && { type: "bold", index: boldMatch.index!, match: boldMatch },
      linkMatch && { type: "link", index: linkMatch.index!, match: linkMatch },
      !linkMatch && urlMatch && { type: "url", index: urlMatch.index!, match: urlMatch },
    ].filter(Boolean).sort((a, b) => a!.index - b!.index);

    if (matches.length === 0) {
      parts.push(remaining);
      break;
    }

    const first = matches[0]!;
    if (first.index > 0) parts.push(remaining.slice(0, first.index));

    if (first.type === "bold") {
      parts.push(<strong key={key++} className="font-bold">{first.match![1]}</strong>);
    } else if (first.type === "link") {
      parts.push(
        <a key={key++} href={first.match![2]} target="_blank" rel="noopener noreferrer" className={linkCls}>
          {first.match![1]}
        </a>
      );
    } else if (first.type === "url") {
      parts.push(
        <a key={key++} href={first.match![0]} target="_blank" rel="noopener noreferrer" className={linkCls}>
          {first.match![0]}
        </a>
      );
    }
    remaining = remaining.slice(first.index + first.match![0].length);
  }

  return parts.length === 1 && typeof parts[0] === "string" ? parts[0] : <>{parts}</>;
}

export default function AnnouncementBanner() {
  const [banner, setBanner] = useState<Banner | null>(initialBanner);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data } = await supabase
        .from("site_settings")
        .select("key, value")
        .in("key", ["announcement_enabled", "announcement_text", "announcement_link_url", "announcement_link_label"]);
      if (cancelled || !data) return;

      const get = (k: string) => data.find((d) => d.key === k)?.value || "";
      const custom = get("announcement_enabled") === "true" && get("announcement_text").trim().length > 0;
      const next: Banner = custom
        ? { text: get("announcement_text"), linkUrl: get("announcement_link_url"), linkLabel: get("announcement_link_label"), isDefault: false }
        : DEFAULT_BANNER;

      try {
        if (custom) localStorage.setItem(CACHE_KEY, JSON.stringify({ text: next.text, linkUrl: next.linkUrl, linkLabel: next.linkLabel }));
        else localStorage.removeItem(CACHE_KEY);
      } catch {
        /* private mode: the next visit just starts from the default */
      }

      setBanner((current) => {
        // Never bring back a strip the visitor has closed, and never re-render for no change.
        if (isDismissed(next)) return null;
        if (current && current.text === next.text && current.linkUrl === next.linkUrl && current.linkLabel === next.linkLabel) return current;
        // Nothing is showing. Only a new announcement the visitor has not seen may appear late;
        // the default line appearing two seconds in would be a layout shift for no reason.
        if (!current && next.isDefault) return null;
        return next;
      });
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (!banner) return null;

  const dismiss = () => {
    try {
      localStorage.setItem(dismissKey(banner), "1");
    } catch {
      /* private mode: it comes back on the next page, acceptable */
    }
    setBanner(null);
  };

  return (
    <div data-announcement className="mb-2">
      {/* Quiet neutral strip. It used to be a bright cyan gradient with white text at about
          2.3:1 contrast, the loudest thing on every page. It sits on the sticky layer of the z-scale,
          above page content and under the navbar's header layer, so it slides under the navbar as the
          page scrolls (it comes after the navbar in the DOM, so an equal z-index would paint it on top). */}
      <div className="relative top-14 lg:top-16 z-sticky bg-secondary text-foreground border-b border-border">
        <div className="container mx-auto px-4 min-h-9 flex items-center justify-center gap-2 text-[13px] sm:text-sm">
          <Megaphone className="h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden="true" />
          {banner.isDefault ? (
            <span className="text-center font-medium">
              <span className="sm:hidden">{renderBannerInline(DEFAULT_BANNER_SHORT)}</span>
              <span className="hidden sm:inline">{renderBannerInline(banner.text)}</span>
            </span>
          ) : (
            <span className="text-center font-medium py-1.5">{renderBannerInline(banner.text, banner.linkUrl, banner.linkLabel)}</span>
          )}
          <button
            type="button"
            onClick={dismiss}
            className="ml-1 shrink-0 inline-flex items-center justify-center w-9 h-9 rounded-full text-muted-foreground hover:text-foreground hover:bg-foreground/10 transition-colors"
            aria-label="Dismiss announcement"
          >
            <X className="h-4 w-4" aria-hidden="true" />
          </button>
        </div>
      </div>
    </div>
  );
}
